import React from "react";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RefreshIcon from "@mui/icons-material/Refresh";
import DynamicFormEditor from "../forms/DynamicFormEditor";
import AgentFileEditor from "../files/AgentFileEditor";
import TaskQueuePanel from "../taskQueue/TaskQueuePanel";

export default function ConversationRightPanel({
  isRightPanelFullscreen,
  shouldStackRightPanel,
  rightPanelFlex = false,
  rightPanelWidth,
  showRightPanelTabBar,
  availableRightTabs,
  activeRightTab,
  setActiveRightTab,
  activeFormContext,
  activeFileContext,
  setActiveFileContext,
  handleCloseDynamicForm,
  handleCloseCreatedFile,
  onCloseTaskQueuePanel,
  handleToggleCurrentFullscreen,
  handleRefreshDynamicForm,
  handleRefreshCreatedFile,
  formRefreshTrigger,
  fileRefreshTrigger,
  taskQueuePanelProps,
}) {
  return (
    <Box
      sx={{
        width: isRightPanelFullscreen
          ? "100%"
          : shouldStackRightPanel
          ? "100%"
          : rightPanelFlex
          ? "auto"
          : `${rightPanelWidth}px`,
        height: isRightPanelFullscreen
          ? "100%"
          : shouldStackRightPanel
          ? "clamp(240px, 45vh, 420px)"
          : "auto",
        maxWidth: "100%",
        flex:
          isRightPanelFullscreen || (rightPanelFlex && !shouldStackRightPanel)
            ? "1 1 0"
            : "0 0 auto",
        borderLeft:
          isRightPanelFullscreen || shouldStackRightPanel
            ? "none"
            : "1px solid",
        borderTop: shouldStackRightPanel ? "1px solid" : "none",
        borderColor: "divider",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
      }}
    >
      {showRightPanelTabBar && (
        <Box
          sx={{
            px: 1,
            pt: 0.5,
            pb: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-end",
            gap: 0,
            flexShrink: 0,
          }}
        >
          {availableRightTabs.map((tab) => (
            <Box
              key={tab}
              onClick={() => setActiveRightTab(tab)}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: "6px 6px 0 0",
                borderBottom:
                  activeRightTab === tab
                    ? "2px solid"
                    : "2px solid transparent",
                borderColor:
                  activeRightTab === tab ? "primary.main" : "transparent",
                backgroundColor:
                  activeRightTab === tab ? "background.paper" : "transparent",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                userSelect: "none",
                color:
                  activeRightTab === tab ? "text.primary" : "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                "&:hover": {
                  backgroundColor:
                    activeRightTab === tab
                      ? "background.paper"
                      : "action.hover",
                },
              }}
            >
              {tab === "form"
                ? "Form"
                : tab === "file"
                ? activeFileContext?.fileName || "File"
                : "Task Queue"}
              <CloseIcon
                onClick={(e) => {
                  e.stopPropagation();
                  if (tab === "form") handleCloseDynamicForm();
                  else if (tab === "file") handleCloseCreatedFile();
                  else if (tab === "taskQueue") onCloseTaskQueuePanel?.();
                }}
                sx={{
                  fontSize: 12,
                  opacity: 0.5,
                  "&:hover": { opacity: 1 },
                  cursor: "pointer",
                }}
              />
            </Box>
          ))}
          <Box sx={{ flex: 1 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              pb: 0.5,
            }}
          >
            <IconButton
              onClick={handleToggleCurrentFullscreen}
              size="small"
              sx={toolbarButtonSx}
            >
              {isRightPanelFullscreen ? (
                <FullscreenExitIcon sx={{ fontSize: 14 }} />
              ) : (
                <FullscreenIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
            {activeRightTab === "form" && activeFormContext?.formId && (
              <IconButton
                onClick={handleRefreshDynamicForm}
                size="small"
                sx={toolbarButtonSx}
              >
                <RefreshIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
            {activeRightTab === "file" &&
              (activeFileContext?.fileId || activeFileContext?.downloadUrl) && (
                <IconButton
                  onClick={handleRefreshCreatedFile}
                  size="small"
                  sx={toolbarButtonSx}
                >
                  <RefreshIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            {activeRightTab === "taskQueue" &&
              taskQueuePanelProps?.onRefresh && (
                <IconButton
                  onClick={taskQueuePanelProps.onRefresh}
                  size="small"
                  sx={toolbarButtonSx}
                >
                  <RefreshIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: activeRightTab === "form" ? "flex" : "none",
          flexDirection: "column",
        }}
      >
        {activeFormContext && (
          <DynamicFormEditor
            formInstanceId={activeFormContext.formId}
            args={activeFormContext.args}
            result={activeFormContext.result}
            refreshTrigger={formRefreshTrigger}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: activeRightTab === "file" ? "flex" : "none",
          flexDirection: "column",
          backgroundColor: "background.default",
          p: 2,
        }}
      >
        {activeFileContext && (
          <AgentFileEditor
            fileContext={activeFileContext}
            refreshTrigger={fileRefreshTrigger}
            onSaved={setActiveFileContext}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: activeRightTab === "taskQueue" ? "flex" : "none",
          flexDirection: "column",
        }}
      >
        {taskQueuePanelProps && <TaskQueuePanel {...taskQueuePanelProps} />}
      </Box>
    </Box>
  );
}

const toolbarButtonSx = {
  width: 28,
  height: 28,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: "divider",
  color: "text.secondary",
  "&:hover": {
    backgroundColor: "action.hover",
    borderColor: "text.disabled",
  },
};
